import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "react-router";

import { ALL_SESSIONS_COPY } from "@/components/session/boxes/session-copy";
import { useAllSessionsBox, type SessionRowJob } from "@/components/session/boxes/use-all-sessions-box";
import { useCancelSessionsBox } from "@/components/session/boxes/use-cancel-sessions-box";
import { useSessionChangeTasks } from "@/components/session/boxes/use-session-change-task";
import { usePlanPlacementTasks } from "@/components/session/boxes/use-plan-placement-tasks";
import { useUnplacedPlansBox } from "@/components/session/boxes/use-unplaced-plans-box";
import { Bento, PageRefusal } from "@/components/ui/box";
import type { Box, Read, TaskBox } from "@/components/ui/box.schema";
import { useAuth } from "@/contexts/AuthContext";
import { attendanceApi, type RegisterSession, type SessionRegister } from "@/lib/api/attendance";
import { courseUnitsApi } from "@/lib/api/courseUnits";
import { ApiError } from "@/lib/api/client";
import { refreshHomeAfterAct } from "@/lib/home-query-keys";
import { courseCalendarHref, courseSessionHref, courseWorkspaceSectionHref } from "@/navigation/course-workspace";
import { courseWorkspacePath, type CourseWorkspaceShell } from "@/navigation/routes";
import { useDeskMemory } from "./desk/desk-memory";
import { courseContentQueries } from "./content/use-course-content";
import { FRAME_COPY, meetingLine } from "./course-frame-copy";
import { useCourseRecord } from "./course-page-boxes";

const copy = ALL_SESSIONS_COPY;
const NO_SESSIONS: RegisterSession[] = [];
const JOBS: readonly SessionRowJob[] = ["prepare", "close", "move", "cancel", "reinstate", "plan"];

/** The register's one read as the kit's (R1): a refusal says nothing it hides, a failed refresh keeps the last read. */
/** `meets` is the course's recorded recurring time, which an empty register says in place of its generic line. */
function registerRead(query: UseQueryResult<SessionRegister>, teaching: boolean, closed: boolean, emptySignal: boolean, meets: string | null): Read<SessionRegister> {
  if (query.isPending) return { status: "loading" };
  if (query.error instanceof ApiError && [401, 403, 404].includes(query.error.status)) return { status: "forbidden" };
  if (!query.data) return { status: "failed", sentence: copy.readFailed, retry: () => void query.refetch() };
  const readAt = new Date(query.dataUpdatedAt || Date.now()).toISOString();
  if (query.isRefetchError) return { status: "stale", last: query.data, readAt, sentence: copy.stale, refresh: () => void query.refetch() };
  // A closed term's empty register says the term is closed, once: beside its own empty sentence, or alone where another view says that.
  // A learner's plans on no session are the List's last rows (C10), so only a course with neither is empty.
  if (!query.data.sessions.length && (teaching || !query.data.undated_plans.length)) return emptySignal ? { status: "empty", sentence: copy.none(teaching), sub: closed ? copy.closedTerm : meets ? FRAME_COPY.session.meets(meets) : copy.noneSub } : { status: "empty", sentence: copy.closedTerm };
  return { status: "ready", value: query.data, readAt };
}

/**
 * C07 All sessions, on the kit: the List of the course's sessions, then
 * Cancel sessions over its selection and Generate sessions for the teaching
 * team. Every session's work opens in its row; there is no sheet (O1).
 */
export function AllSessionsRegister({ courseId, timezone, teaching, canEdit = false, termOpen = false, shell, generate = null, emptySignal = true, unplaced = false, calendarOrder = false }: {
  courseId: string;
  timezone: string;
  teaching: boolean;
  canEdit?: boolean;
  termOpen?: boolean;
  shell: CourseWorkspaceShell;
  /** The teaching team's Generate sessions, when the course has meeting days. */
  generate?: TaskBox<null> | null;
  /** Whether a course with no sessions says so here; a view that says it elsewhere passes false. */
  emptySignal?: boolean;
  /** The teaching team's plans not on a session yet (C08), after the sessions. */
  unplaced?: boolean;
  /**
   * The Desk's Calendar order (#1393 M2): the current or next session is
   * marked, Find has "Cancelled only", and the find, that filter and the bulk
   * selection are kept in the Desk entry's history so Return restores them.
   */
  calendarOrder?: boolean;
}) {
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const desk = useDeskMemory();
  const memory = calendarOrder ? desk : null;
  const [selected, setSelectedState] = useState<string[]>(() => memory?.live.selected ?? []);
  const setSelected = useCallback((next: string[] | ((current: string[]) => string[])) => {
    setSelectedState((current) => {
      const value = typeof next === "function" ? next(current) : next;
      if (memory) memory.live.selected = value;
      return value;
    });
  }, [memory]);
  const [cancelledOnly, setCancelledOnly] = useState(() => memory?.live.cancelledOnly === true);
  // The row left from its open detail reopens once, as this mount starts; a
  // later remount (a mode switch) after that return starts closed.
  const [reopen] = useState(() => (memory && !memory.returned && memory.restored.opener?.box === "all-sessions" && memory.restored.opener.detail ? memory.restored.opener.row : null));
  const query = useQuery({
    queryKey: ["course-session-register", me?.tenant_id, me?.user_id, courseId],
    queryFn: () => attendanceApi.sessionRegister(courseId),
    // Only this course's sessions are ever drawn, whatever a response carries.
    select: (register: SessionRegister) => ({ ...register, sessions: register.sessions.filter((session) => session.course_id === courseId) }),
    retry: false,
  });
  const closed = teaching && !termOpen;
  // A course with no dated session still says when it meets, once recorded (the retired Home session box's line),
  // read only when the register's empty line is the one that would say it.
  const empty = query.data?.sessions.length === 0;
  const recorded = useCourseRecord(courseId, empty && emptySignal && !closed && (teaching || !query.data?.undated_plans.length));
  const meets = empty && recorded.data?.id === courseId ? meetingLine(recorded.data) : null;
  const read = registerRead(query, teaching, closed, emptySignal, meets);
  // Control+K arrives here to find a session: Find takes focus once the sessions are read.
  const focusFind = useLocation().state?.focusSessionFind === true;
  const ready = read.status === "ready";
  useEffect(() => {
    if (focusFind && ready) document.querySelector<HTMLInputElement>('[data-box-id="all-sessions"] input[type="search"]')?.focus();
  }, [focusFind, ready]);
  // A row an address opened for its work, such as the session page's Move plan, comes into view once drawn, its open work focused.
  // The selected session alone is the workspace's context, and leaves focus with the page.
  const arrival = useRef(params.get("session") && params.get("change") ? { box: "all-sessions", key: params.get("session")! } : params.get("plan") ? { box: "unplaced-plans", key: params.get("plan")! } : null);
  useEffect(() => {
    const target = arrival.current;
    const row = target && document.querySelector<HTMLElement>(`[data-box-id="${target.box}"] [data-row="${target.key}"]`);
    if (!row) return;
    arrival.current = null;
    row.querySelector<HTMLElement>('[tabindex="-1"]')?.focus({ preventScroll: true });
    row.firstElementChild?.scrollIntoView?.({ block: "center" });
  });
  const canAct = teaching && canEdit && termOpen;
  const courseHref = courseWorkspacePath(shell, courseId);
  const { refetch } = query;
  // A change moves the session everywhere it is read: its page, the course, the week and Home.
  const refresh = useCallback(async () => {
    await refetch();
    refreshHomeAfterAct(queryClient);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["session-workspace"] }),
      queryClient.invalidateQueries({ queryKey: ["course-home"] }),
      queryClient.invalidateQueries({ queryKey: ["course-workspace-sessions"] }),
      queryClient.invalidateQueries({ queryKey: ["attendance", "course", courseId] }),
      queryClient.invalidateQueries(courseContentQueries(courseId)),
    ]);
  }, [refetch, queryClient, courseId]);
  // A plan saved or published in any editor on the page moves its row's word: read again when the plan list does.
  const units = useQuery({ queryKey: ["course-units", courseId], queryFn: () => courseUnitsApi.list(courseId), enabled: false });
  useEffect(() => {
    if (units.dataUpdatedAt) void refetch();
  }, [units.dataUpdatedAt, refetch]);
  const unpublished = query.data?.course_status === "draft";
  const changeTask = useSessionChangeTasks({ courseId, timezone, unpublished, onChanged: refresh });
  const placeTask = usePlanPlacementTasks({ courseId, timezone, sessions: query.data?.sessions ?? NO_SESSIONS, onChanged: refresh });
  const requested = params.get("change");
  const course = { id: courseId, timezone, term_open: termOpen };
  const sessionHref = (sessionId: string) => courseSessionHref(courseHref, sessionId);
  const all = useAllSessionsBox({
    read,
    timezone,
    teaching,
    canAct,
    termOpen,
    courseworkPath: (sessionId) => courseWorkspaceSectionHref(courseHref, "coursework", sessionId),
    sessionHref,
    selected,
    onToggle: (sessionId) => setSelected((current) => (current.includes(sessionId) ? current.filter((id) => id !== sessionId) : [...current, sessionId])),
    changeTask,
    placeTask,
    onCommitted: () => void refresh(),
    // Returning to the Desk reopens the row the person left from, so its own link takes focus (contract 4).
    openSessionId: params.get("session") ?? reopen,
    openJob: JOBS.find((job) => job === requested) ?? null,
    marks: calendarOrder,
    ...(memory ? {
      initialQuery: memory.live.find ?? "",
      onQuery: (find: string) => { memory.live.find = find; },
      cancelledOnly: { value: cancelledOnly, onChange: (value: boolean) => { memory.live.cancelledOnly = value; setCancelledOnly(value); } },
    } : {}),
  });
  const sessions = query.data?.sessions ?? [];
  const bulk = useCancelSessionsBox({
    courseId,
    timezone,
    unpublished,
    list: all.box,
    sessions,
    selected,
    hidden: query.data ? all.hidden(query.data) : 0,
    onSelect: setSelected,
    onChanged: refresh,
  });
  const plans = useUnplacedPlansBox({
    read,
    register: query.data,
    course,
    editable: canAct,
    openPlan: params.get("plan"),
    newPlanHref: courseCalendarHref(courseHref, { plan: "new" }),
    sessionHref,
    place: placeTask,
    onChanged: () => void refresh(),
    onNewClosed: () => {
      const next = new URLSearchParams(params);
      next.delete("plan");
      setParams(next, { replace: true });
    },
  });
  const showPlans = unplaced && read.status !== "forbidden" && (canAct || Boolean(query.data?.undated_plans.length));
  const boxes: Box[] = [
    ...(read.status === "empty" && !emptySignal && !closed ? [] : [all.box as Box]),
    // The page keeps its boxes in one order whatever the register reads (P4): Cancel sessions rests until sessions are selected.
    ...(canAct ? [bulk as Box] : []),
    // A refused register refuses the course's sessions whole: no Generate beside it (R1).
    ...(generate && read.status !== "forbidden" ? [generate as Box] : []),
    ...(showPlans ? [plans as Box] : []),
  ];
  // A refused register refuses the course's sessions whole: one sentence and the way back (R1).
  if (read.status === "forbidden")
    return <PageRefusal id={`all-sessions-${courseId}`} journey={teaching ? "course-sessions-teaching" : "course-sessions-learning"}
      refusal={{ reason: "forbidden", sentence: ALL_SESSIONS_COPY.forbidden, back: { label: ALL_SESSIONS_COPY.forbiddenBack, to: courseHref } }} />;
  if (!boxes.length) return null;
  return (
    <>
      <Bento id={`all-sessions-${courseId}`} journey={teaching ? "course-sessions-teaching" : "course-sessions-learning"} zone={timezone} boxes={boxes} onRowOpenChange={all.onRowOpenChange} />
      {all.hosts}
    </>
  );
}
